import { Button, Select, Space } from 'antd';
import React, { useEffect, useMemo } from 'react';
import { useEventListForCreateMetric } from '@src/emp-3.0/api/queryHooks';
import { LabelBuilder } from '@src/emp-3.0/components/Metrics/components';
import { CheckboxWrapper } from './styled';
import { filterEventOptionsByEventType } from '@src/emp-3.0/utils/buildOptions';
import { EEventType, MetricDataScope } from '@src/emp-3.0/types';
import { cloneDeep } from 'lodash';

interface IProps {
  eventType?: EEventType;
  dataScope?: MetricDataScope[];
  mode?: 'multiple' | 'tags';
  value?: string;
  // raw event will be passed event name as string, mapped event will be passed event id as number
  onChange?: (value: number | string | null | (number | string)[], event: any) => void;
  disabledOptions?: string[];
  defaultSelectedValue?: (number | string)[];
  /** Check whether current value is a member of mapped event list */
  checkValue?: boolean;
}
const EventsSelect: React.FC<IProps> = ({
  eventType,
  dataScope,
  mode,
  value,
  onChange,
  disabledOptions,
  defaultSelectedValue,
  checkValue,
  ...rest
}) => {
  const { data = [], isLoading, isFetching } = useEventListForCreateMetric();
  const [open, setOpen] = React.useState(false);
  const [selectedValues, setSelectedValues] = React.useState<(number | string)[]>([]);
  const [currentSelectedValues, setCurrentSelectedValues] = React.useState<(number | string)[]>([]);
  const [searchText, setSearchText] = React.useState('');
  useEffect(() => {
    if (defaultSelectedValue) {
      setSelectedValues(defaultSelectedValue);
      setCurrentSelectedValues(defaultSelectedValue);
    }
  }, [defaultSelectedValue]);

  const options = useMemo(() => {
    const validData = filterEventOptionsByEventType(data, eventType).filter((it) => {
      if (!dataScope) {
        return true;
      }
      return (
        (dataScope.includes('event_summary') && it.name !== 'conviva_network_request') ||
        (dataScope.includes('service_flow') && it.name === 'conviva_network_request')
      );
    });
    const options = validData
      .filter((event) => event.name.toLowerCase().includes(searchText.toLowerCase()))
      .map((event, index) => {
        const optionValue = event.id || event.name;
        const disabled =
          disabledOptions?.includes(event.id) || selectedValues.includes(optionValue);
        const added = selectedValues.includes(optionValue);
        const key = `${event.name}_${index}`;
        if (mode === 'multiple') {
          return {
            label: (
              <CheckboxWrapper
                checked={currentSelectedValues.includes(optionValue)}
                disabled={disabled}
                onChange={(e) => {
                  const newValue = e.target.checked
                    ? [...currentSelectedValues, optionValue]
                    : currentSelectedValues.filter((val) => val !== optionValue);
                  setCurrentSelectedValues(newValue);
                }}
              >
                <LabelBuilder
                  event={event}
                  suffix={
                    added ? (
                      <span style={{ color: 'rgba(0,0,0,.45)', display: 'inline-block' }}>
                        Added
                      </span>
                    ) : event.is_recently_used ? (
                      <span style={{ color: 'rgba(0,0,0,.45)', display: 'inline-block' }}>
                        Recently Used
                      </span>
                    ) : event.is_raw ? (
                      ''
                    ) : (
                      <span style={{ color: 'rgba(0,0,0,.45)', display: 'inline-block' }}>
                        Mapped
                      </span>
                    )
                  }
                  quickLookProps={{
                    searchText: '',
                    okButtonProps: {},
                    noButtonProps: {},
                    footer: null,
                  }}
                />
              </CheckboxWrapper>
            ),
            text: event.name,
            group: event.group,
            value: optionValue,
            event,
            key,
            disabled,
          };
        }
        return {
          label: (
            <LabelBuilder
              event={event}
              suffix={
                event.is_recently_used ? (
                  <span style={{ color: 'rgba(0,0,0,.45)', display: 'inline-block' }}>
                    Recently Used
                  </span>
                ) : event.is_raw ? (
                  ''
                ) : (
                  <span style={{ color: 'rgba(0,0,0,.45)', display: 'inline-block' }}>Mapped</span>
                )
              }
              quickLookProps={{
                searchText: '',
                okButtonProps: {},
                noButtonProps: {},
                footer: null,
              }}
            />
          ),
          text: event.name,
          group: event.group,
          value: optionValue,
          key,
          disabled,
          event,
        };
      });
    return options.sort((a, b) => a.disabled - b.disabled);
  }, [
    data,
    disabledOptions,
    mode,
    currentSelectedValues,
    selectedValues,
    searchText,
    eventType,
    dataScope,
  ]);

  /** if current value is not a member of options, change it to null */
  useEffect(() => {
    if (!checkValue || !value || !Array.isArray(options) || options.length === 0 || isFetching) {
      return;
    }
    const exist = options.some((it) => it.value === value);
    if (!exist) {
      onChange?.(null, null);
    }
  }, [checkValue, value, options, onChange, isFetching]);

  const handleCancel = () => {
    setSearchText('');
    setOpen(false);
  };

  const handleApply = () => {
    const selected = [...new Set([...currentSelectedValues, ...selectedValues])];
    const events: any[] = [];
    selected.forEach((i) => {
      if (typeof i === 'string') {
        // raw event
        events.push(i);
      } else {
        const event: any = data.find((e) => e.id === i);
        if (event) {
          events.push(event);
        }
      }
    });
    onChange?.(selected, events);

    setSearchText('');
    setOpen(false);
  };
  return (
    <Select
      loading={isLoading}
      mode={mode}
      style={{ flex: '1 0 0', minWidth: '300px' }}
      placeholder={'Search events'}
      options={options}
      showSearch
      allowClear={!(mode === 'multiple' || mode === 'tags')}
      searchValue={searchText}
      value={mode === 'multiple' || mode === 'tags' ? [] : null}
      {...rest}
      dropdownRender={
        mode === 'multiple'
          ? (menu) => (
              <div>
                <div>{menu}</div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: 8,
                    borderTop: '1px solid #e8e8e8',
                  }}
                >
                  <Space>
                    <Button size="small" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button type="primary" size="small" onClick={handleApply}>
                      Apply
                    </Button>
                  </Space>
                </div>
              </div>
            )
          : undefined
      }
      open={open}
      onSelect={(value, option) => {
        if (mode !== 'multiple') {
          if (typeof value === 'string') {
            // raw event
            onChange?.(value, value);
          } else {
            onChange?.(value, option.event);
          }
        }
      }}
      onSearch={(val) => setSearchText(val)}
      onDropdownVisibleChange={(open) => {
        if (!open) {
          setCurrentSelectedValues(cloneDeep(selectedValues));
          setSearchText('');
        }
        setOpen(open);
      }}
      filterOption={false}
    />
  );
};

export default EventsSelect;
